import { Badge } from "@/components/ui/badge";
import { Clock, Users, Baby, Bus, Truck, AlertTriangle, Lock, BadgeDollarSign, StickyNote, Layers, ClipboardList } from "lucide-react";
import { TIPOS_MODALIDAD } from "@/lib/constants";
import { ALERTA } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";

function vehiculosLabel(s) {
  if (s.vehiculos && s.vehiculos.length > 0) {
    const counts = {};
    for (const v of s.vehiculos) counts[v.tipo] = (counts[v.tipo] || 0) + 1;
    return Object.entries(counts)
      .map(([tipo, n]) => `${n} ${tipo === "bus" ? "Bus" : "Van"}`)
      .join(" + ");
  }
  return s.vehiculo === "van" ? "Van" : "Bus";
}

function miembroChip(m, mostrarTour) {
  const idioma = m.idioma === "combinada" ? "Bilingüe" : m.idioma === "en" ? "EN" : "ES";
  const veh = m.vehiculo === "van" ? "Van" : "Bus";
  const modalidad = TIPOS_MODALIDAD[m.modalidad] || m.modalidad;
  const prefijo = mostrarTour && m.tour_nombre ? `${nombreTourDisplay(m.tour_nombre, m.modalidad)} · ` : "";
  return `${prefijo}${m.cupo_usado} ${modalidad} ${veh} (${idioma})`;
}

export default function SalidaCard({ s, onClick }) {
  const a = ALERTA[s.alerta] || ALERTA.verde;
  const VehiculoIcon = s.vehiculo === "van" ? Truck : Bus;
  const idiomaLabel = s.idioma === "combinada" ? "Combinada" : s.idioma === "en" ? "Inglés" : "Español";
  const esFantasma = s.programada === false;

  return (
    <button
      onClick={() => onClick(s)}
      data-testid={esFantasma ? `salida-fantasma-${s.tour_id}-${s.hora}` : `salida-card-${s.id}`}
      className={`group relative w-full text-left rounded-xl border bg-white p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 ${a.border} ${esFantasma ? "opacity-80 hover:opacity-100" : ""}`}
    >
      {!esFantasma && <span className={`absolute left-0 top-3 bottom-3 w-1 rounded-full ${a.bar}`} />}

      <div className="flex items-start justify-between gap-3 pl-2.5">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-slate-900">
            <Clock className="h-4 w-4 text-slate-400" />
            <span className="font-heading font-bold text-lg tabular-nums">{s.hora}</span>
            {s.nota && (
              <StickyNote
                className="h-3.5 w-3.5 text-amber-500"
                title={s.nota}
                data-testid={`salida-nota-icon-${s.id}`}
              />
            )}
          </div>
          <p className="font-medium text-slate-900 mt-1 truncate" title={nombreTourDisplay(s.tour_nombre, s.modalidad)}>
            {nombreTourDisplay(s.tour_nombre, s.modalidad)}
          </p>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {s.es_grupo ? (
              <Badge variant="outline" className="text-[10px] font-medium gap-1 border-indigo-200 text-indigo-700 bg-indigo-50" data-testid={`salida-grupo-badge-${s.id}`}>
                <Layers className="h-3 w-3" /> Combinado · {s.miembros.length} salidas
              </Badge>
            ) : (
              <>
                <Badge variant="outline" className="text-[10px] font-medium">
                  {TIPOS_MODALIDAD[s.modalidad] || s.modalidad}
                </Badge>
                <Badge variant="outline" className="text-[10px] font-medium gap-1" data-testid={`salida-vehiculos-${s.id}`}>
                  <VehiculoIcon className="h-3 w-3" /> {vehiculosLabel(s)}
                </Badge>
                {!esFantasma && (
                  <Badge
                    variant="outline"
                    className={`text-[10px] font-medium uppercase ${s.idioma === "combinada" ? "border-indigo-200 text-indigo-700 bg-indigo-50" : ""}`}
                    data-testid={`salida-idioma-${s.id}`}
                  >
                    {idiomaLabel}
                  </Badge>
                )}
              </>
            )}
          </div>
          {s.idioma === "combinada" && s.idioma_breakdown && !s.es_grupo && (
            <p className="text-[10px] text-slate-400 mt-1" data-testid={`salida-idioma-breakdown-${s.id}`}>
              {s.idioma_breakdown.en} EN + {s.idioma_breakdown.es} ES
            </p>
          )}
          {s.es_grupo && (
            <p className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-x-1.5" data-testid={`salida-grupo-desglose-${s.id}`}>
              {(() => {
                const toursDistintos = new Set(s.miembros.map((m) => m.tour_nombre)).size > 1;
                return s.miembros.map((m) => <span key={m.id}>{miembroChip(m, toursDistintos)}</span>);
              })()}
            </p>
          )}
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${a.chip}`}
          data-testid={esFantasma ? `salida-fantasma-alerta-${s.tour_id}-${s.hora}` : `salida-alerta-${s.id}`}
        >
          {s.alerta === "rojo" && <AlertTriangle className="h-3 w-3" />}
          {s.alerta === "cerrada" && <Lock className="h-3 w-3" />}
          {a.label}
        </span>
      </div>

      {esFantasma ? (
        <p className="pl-2.5 mt-3 text-[11px] text-slate-400">
          Horario del catálogo sin salida creada todavía. Hacé clic para programarlo.
        </p>
      ) : s.es_privado ? (
        <div className="pl-2.5 mt-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-slate-500 inline-flex items-center gap-1">
              <ClipboardList className="h-3.5 w-3.5" /> Por solicitud (no numérico)
            </span>
            <span className={`text-sm font-bold tabular-nums ${a.text}`} data-testid={`salida-solicitudes-${s.id}`}>
              {s.reservas_count} solicitud{s.reservas_count === 1 ? "" : "es"}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-[11px]">
            <span className="inline-flex items-center gap-1 text-slate-600">
              <Users className="h-3.5 w-3.5 text-slate-400" />
              {s.pax_solicitados} pasajero(s) solicitados
            </span>
            {s.solicitudes_pendientes > 0 && (
              <span className="inline-flex items-center gap-1 text-amber-700 font-medium bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                {s.solicitudes_pendientes} pendiente(s) de aceptar/rechazar
              </span>
            )}
          </div>
        </div>
      ) : (
        <div className="pl-2.5 mt-3">
          <div className="flex items-end justify-between mb-1">
            <span className="text-xs text-slate-500">Ocupación</span>
            <span className={`text-sm font-bold tabular-nums ${a.text}`} data-testid={`salida-ocupacion-${s.id}`}>
              {s.cupo_usado} / {s.cupo_max || "—"}
            </span>
          </div>
          <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${a.bar} transition-all duration-500`} style={{ width: `${Math.min(100, s.pct)}%` }} />
          </div>

          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2.5 text-[11px]">
            <span className="inline-flex items-center gap-1 text-slate-600">
              <Users className="h-3.5 w-3.5 text-slate-400" />
              {s.adultos} ad · {s.ninos} niñ
            </span>
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-medium ${
                s.infantes > 0
                  ? s.infante_cuenta
                    ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200"
                    : "bg-slate-100 text-slate-500"
                  : "text-slate-400"
              }`}
              title={
                s.infantes > 0
                  ? s.infante_cuenta
                    ? "Los infantes OCUPAN asiento en esta salida"
                    : "Los infantes NO ocupan asiento en esta salida"
                  : "Sin infantes"
              }
              data-testid={`salida-infantes-${s.id}`}
            >
              <Baby className="h-3.5 w-3.5" />
              {s.infantes} inf{s.infantes > 0 ? (s.infante_cuenta ? " · ocupan" : " · no ocupan") : ""}
            </span>
            {s.saldo_count > 0 && (
              <span className="inline-flex items-center gap-1 text-rose-600 font-medium">
                <BadgeDollarSign className="h-3.5 w-3.5" />
                {s.saldo_count} con saldo
              </span>
            )}
          </div>
        </div>
      )}
    </button>
  );
}
